import { Injectable, inject } from '@angular/core';
import { HttpContextToken, HttpRequest } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QUIET_ERRORS } from '@client/app/business-objects/base';
import createDebug from 'debug';
import { ErrorDialogComponent } from '@components/error-dialog/error-dialog.component';

const Debug = createDebug('ErrorDialogService');

/**
 * Error Dialog
 *
 * Client-side surface for failed requests. The error-dialog interceptor
 * hands every failed request here. A failed write shows the server's
 * message in the ErrorDialog component, appending to the open dialog
 * rather than stacking new ones. A read that fails with a server error or
 * no response shows a load failure snackbar. The service also exposes the
 * HttpContext token that opts a request out of both, for pages that handle
 * errors themselves. Patch showMessage or showLoadFailure to change how
 * either is shown, or handleHttpError to change which failures surface.
 */
@Injectable({ providedIn: 'root' })
export class ErrorDialogService {
  /**
   * Exclude - HttpContext token that opts a request out of the error dialog
   * and the load failure snackbar. Set it on requests whose errors the page
   * handles itself. It is the QUIET_ERRORS token a persisted array or form
   * group sets for its reads when its quietErrors is true.
   */
  exclude: HttpContextToken<boolean> = QUIET_ERRORS;

  private matDialog = inject(MatDialog);

  /** Dialog Ref - The open error dialog, or undefined when none is open */
  dialogRef?: any;

  private matSnackBar = inject(MatSnackBar);

  /** Snack Bar Ref - The open load failure snackbar, or undefined when none is open */
  snackBarRef?: any;

  /**
   * Handle Http Error - Show a failed write's server message in the error
   * dialog, and a read that failed with a 5xx or no response in the load
   * failure snackbar. Skips 4xx reads (the page shows its own empty state),
   * excluded requests, 401s (the auth flow owns those), and non-HTTP
   * errors. Errors are checked structurally rather than via instanceof so
   * callers can hand in any rejection the HTTP pipeline produces.
   */
  handleHttpError(req: HttpRequest<unknown>, error: unknown): void {
    const debug = Debug.extend('handleHttpError');
    // Stand aside for a request that opted out
    if (req.context.get(this.exclude)) return;

    /**
     * Ignore anything that is not an HTTP failure
     *
     * Checked structurally rather than with instanceof so callers can hand in
     * any rejection the HTTP pipeline produces.
     */
    const httpError = error as {
      status?: unknown;
      error?: unknown;
      message?: unknown;
    } | null;
    if (!httpError || typeof httpError.status !== 'number') return;

    // Let the auth flow own an unauthenticated response
    if (httpError.status === 401) return;

    // A failed read is a page that didn't load
    if (
      req.method === 'GET' ||
      req.method === 'HEAD' ||
      req.method === 'OPTIONS'
    ) {
      /**
       * Leave a 4xx read to the page
       *
       * A read that finds nothing shows the page's own empty or not-found
       * state. Only a failure the page can't explain is reported.
       */
      if (httpError.status !== 0 && httpError.status < 500) return;

      /**
       * Report the load failure in the snackbar
       *
       * A gateway's 5xx body is an HTML page, not a message, so the status is
       * shown instead; status 0 means the request never reached the server.
       */
      const loadMessage =
        httpError.status === 0
          ? "Couldn't reach the server. Check your connection and try again."
          : `The server couldn't load this data (${httpError.status}). Try again in a moment.`;
      debug('handling load failure for %s %j', req.method, httpError.status);
      this.showLoadFailure(loadMessage);
      return;
    }

    /**
     * Prefer the server's message, then the transport's
     *
     * The server's error string is the one written for a person to read; the
     * transport message covers a connection failure that never reached it.
     */
    const body = httpError.error as { error?: unknown } | null;
    const message =
      body && typeof body.error === 'string' && body.error
        ? body.error
        : typeof httpError.message === 'string' && httpError.message
          ? httpError.message
          : 'Request failed';

    // Surface the message in the dialog
    debug(
      'handling http error for %s %j: %j',
      req.method,
      httpError.status,
      message,
    );
    this.showMessage(message);
  }

  /** Show Message - Open the error dialog with the message, or append it when the dialog is already open */
  showMessage(message: string): void {
    const debug = Debug.extend('showMessage');
    /**
     * If a dialog is already open
     *
     * A second dialog would cover the first, so a burst of failed writes reads
     * as one list instead of a stack the user has to dismiss one at a time.
     */
    if (this.dialogRef) {
      // Replace the array so the open dialog renders the added message
      debug('appending message to open dialog: %j', message);
      const instance = this.dialogRef.componentInstance;
      instance.messages = [...instance.messages, message];
      return;
    }

    // Open the dialog with this message as its only entry
    debug('opening error dialog with message: %j', message);
    this.dialogRef = this.matDialog.open(ErrorDialogComponent.contentComponent);
    this.dialogRef.componentInstance.messages = [message];

    // Forget the reference on close so the next message opens a fresh dialog
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  /**
   * Show Load Failure - Tell the user a page's data didn't load, in a
   * snackbar rather than a dialog: a failed load needs noticing, not
   * acknowledging. While one is open, further failures add nothing, so an
   * outage that fails several reads at once shows a single snackbar.
   */
  showLoadFailure(message: string): void {
    const debug = Debug.extend('showLoadFailure');
    // One snackbar covers every load that fails while it is open
    if (this.snackBarRef) return;

    // Open the snackbar with the message
    debug('opening load failure snackbar: %j', message);
    this.snackBarRef = this.matSnackBar.open(message, 'Dismiss', {
      duration: 10000,
    });

    // Forget the reference on dismiss so the next failure opens a fresh one
    this.snackBarRef.afterDismissed().subscribe(() => {
      this.snackBarRef = undefined;
    });
  }
}
